import {
  BackComponent,
  FormCheckBox,
  FormInput,
  Loader,
  PageMainContent,
  Typography,
} from '@/components'
import { transform } from './helpers'
import { FormProvider, useSnackbar } from '@/providers'
import { usePayment, useUpdatePayment } from '@/services'
import { useGoBack } from '@/utils'
import { Button } from '@surein/ui'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { PageActions, PageTitle } from './styles'
import { usePaymentMethodValidation } from './usePaymentMethodValidation'

interface PageFormProps {
  data: EditPaymentFormValues
}

export interface EditPaymentFormValues {
  name: string
  iban: string
  bic: string
  priority: boolean
}

const PageForm: React.FC<PageFormProps> = ({ data }) => {
  const { t } = useTranslation()
  const { paymentId } = useParams<{ paymentId: string }>()
  const snackbar = useSnackbar()
  const goBack = useGoBack()
  const createUpdatePaymentMutation = useUpdatePayment(paymentId ?? '')
  const validationSchema = usePaymentMethodValidation()

  const handleSubmit = (values: EditPaymentFormValues) => {
    createUpdatePaymentMutation
      .mutateAsync({ ...values, priority: values.priority ? 1 : 0 })
      .then((response: any) => {
        if (response.rollbackPaymentMethodId !== null) {
          snackbar.info(t('snackbars.info.paymentUpdatedPartially'))
        } else {
          snackbar.success(t('snackbars.success.updatePayment'))
        }
        goBack()
      })
      .catch(() => {
        snackbar.error(t('snackbars.error.generalMsg'))
        goBack()
      })
  }

  return (
    <FormProvider
      initialValues={data}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
    >
      <FormInput label={t('profile.payments.name.label')} name="name" />
      <FormInput label={t('profile.payments.iban.label')} name="iban" />
      <PageActions>
        <FormCheckBox
          label={t('profile.payments.isStandard')}
          name="priority"
        />
      </PageActions>
      <PageActions>
        <Button onClick={goBack} variant="muted" shape="square">
          {t('common.cancel')}
        </Button>
        <Button type="submit" variant="primary" shape="square">
          {t('common.save')}
        </Button>
      </PageActions>
    </FormProvider>
  )
}

export const EditPaymentPage: React.FC = () => {
  const { t } = useTranslation()
  const { paymentId } = useParams<{ paymentId: string }>()
  const { data, isLoading } = usePayment(paymentId || '', {
    select: transform,
    enabled: !!paymentId,
  })

  if (isLoading || !data) return <Loader />

  const typedData = (data as unknown) as EditPaymentFormValues

  return (
    <>
      <PageMainContent size="sm">
        <BackComponent />
        <PageTitle>
          <Typography variant="h4">
            {t('profile.payments.editPayment')}
          </Typography>
        </PageTitle>
        <PageForm data={typedData} />
      </PageMainContent>
    </>
  )
}
